import axios from 'axios';

// Get API base URL based on environment
const getApiBaseUrl = () => {
  // Check for explicit environment variable first (set at build time for Docker)
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '') {
    const apiUrl = process.env.REACT_APP_API_URL;
    // Remove trailing /api if present to avoid double /api/api
    const cleanUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
    console.log('🔧 Using REACT_APP_API_URL:', cleanUrl);
    return cleanUrl;
  }
  
  // Auto-detect for local development
  const port = window.location.port;
  
  // Development with React dev server (port 3000)
  if (port === '3000') {
    console.log('🔧 Development mode (port 3000) - using empty baseURL for proxy');
    return ''; // Uses proxy configuration from package.json
  }
  
  // Fallback for any other scenario - use relative /api path
  console.log('🌐 Using relative path /api');
  return '/api';
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔐 Token attached: ${token.substring(0, 20)}...`);
    } else {
      console.warn('⚠️ No token found in localStorage');
      console.warn('⚠️ Keys in localStorage:', Object.keys(localStorage));
    }
    
    // Ensure URL starts with /api if using proxy or relative paths
    const baseURL = config.baseURL || '';
    if (config.url && !config.url.startsWith('/api') && !config.url.startsWith('http')) {
      config.url = `/api${config.url}`;
    }
    
    // Always log API calls for debugging
    console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${baseURL}${config.url}`);
    console.log('🔍 Headers:', config.headers);
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url
    });
    
    // Log FULL response for debugging (includes debug field)
    console.error('🔍 FULL ERROR RESPONSE:', error.response?.data);
    
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      console.log('🔐 401 Unauthorized - clearing token and redirecting to login');
      localStorage.removeItem('token');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Export helper to get current API URL (for debugging)
export const getCurrentApiUrl = () => {
  const baseURL = getApiBaseUrl();
  console.log('🔧 Current API Base URL:', baseURL || 'Relative URLs (same origin)');
  return baseURL;
};

export default api;
export { getApiBaseUrl };
